import type * as React from "react";
import {
  AlertCircle, AlertTriangle, ArrowDown, ArrowLeft, ArrowRight, BadgeCheck, Bath, BedDouble, Bell, Bookmark,
  Building2, Bus, Calendar, CalendarCheck, CalendarClock, CalendarDays, Camera, ChartColumn, ChartPie, Check,
  CheckCheck, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp, ClipboardList,
  Clock, Copy, Download, EllipsisVertical, ExternalLink, Eye, FileText, Filter, Folder, Globe, HandCoins,
  Heart, House, Image, Inbox, Info, Kanban, Key, KeyRound, LayoutDashboard, LayoutGrid, LineChart, Link, List,
  Loader2, Lock, LogOut, Mail, MailCheck, MailQuestion, MailX, Map, MapPin, Menu, MapPinned, Megaphone,
  MessageCircle, MessageSquare, Minus, PackageMinus, PanelLeftClose, PanelLeftOpen, Pencil, Phone, Play, Plus,
  QrCode, Quote, RefreshCw, Rotate3d, RotateCcw, Ruler, ScanLine, School, Search, Send, Settings2, Share,
  Share2, ShoppingBag, ShoppingCart, Signpost, Smartphone, Sparkles, SunMoon, TrainFront, Trash2, Trees,
  TrendingDown, TrendingUp, Truck, Upload, User, UserPlus, UserRoundPlus, Users, UserX, Video, View, X
} from "lucide-react";

import { cx } from "../../lib/cx";
import s from "./Icon.module.css";

/* Lucide vía `lucide-react`, con los SVG renderizados en SSR.
 *
 * Los iconos se declaran de a uno a propósito: `import * as lucide` mataría el
 * tree-shaking y se llevaría los ~6000 iconos del paquete al bundle de la
 * landing pública. PARA USAR UNO NUEVO hay que sumarlo al import y al mapa;
 * `IconName` sale del mapa, así que un nombre que no está rompe el typecheck
 * en vez de no dibujarse en silencio. */
/* WhatsApp no está en Lucide (sacó los logos de marcas). El glifo es el de
 * Simple Icons (CC0), relleno con `currentColor`, con la misma firma que un
 * icono Lucide para entrar al mapa como uno más. */
function WhatsApp({
  size = 24,
  strokeWidth: _strokeWidth,
  absoluteStrokeWidth: _absoluteStrokeWidth,
  ...rest
}: { size?: number | string; strokeWidth?: number | string; absoluteStrokeWidth?: boolean } & React.SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...rest}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  );
}

const ICONS = {
  "alert-circle": AlertCircle,
  "alert-triangle": AlertTriangle,
  "arrow-down": ArrowDown,
  "arrow-left": ArrowLeft,
  "arrow-right": ArrowRight,
  "badge-check": BadgeCheck,
  bath: Bath,
  "bed-double": BedDouble,
  bell: Bell,
  bookmark: Bookmark,
  "building-2": Building2,
  bus: Bus,
  calendar: Calendar,
  "calendar-check": CalendarCheck,
  "calendar-clock": CalendarClock,
  "calendar-days": CalendarDays,
  camera: Camera,
  "chart-column": ChartColumn,
  "chart-pie": ChartPie,
  check: Check,
  "check-check": CheckCheck,
  "check-circle-2": CheckCircle2,
  "chevron-down": ChevronDown,
  "chevron-left": ChevronLeft,
  "chevron-right": ChevronRight,
  "chevron-up": ChevronUp,
  "chevrons-up-down": ChevronsUpDown,
  "clipboard-list": ClipboardList,
  clock: Clock,
  copy: Copy,
  download: Download,
  "ellipsis-vertical": EllipsisVertical,
  "external-link": ExternalLink,
  eye: Eye,
  "file-text": FileText,
  filter: Filter,
  folder: Folder,
  globe: Globe,
  "hand-coins": HandCoins,
  heart: Heart,
  house: House,
  image: Image,
  inbox: Inbox,
  info: Info,
  kanban: Kanban,
  key: Key,
  "key-round": KeyRound,
  "layout-dashboard": LayoutDashboard,
  "layout-grid": LayoutGrid,
  "line-chart": LineChart,
  link: Link,
  list: List,
  "loader-2": Loader2,
  lock: Lock,
  "log-out": LogOut,
  mail: Mail,
  "mail-check": MailCheck,
  "mail-question": MailQuestion,
  "mail-x": MailX,
  map: Map,
  "map-pin": MapPin,
  "map-pinned": MapPinned,
  megaphone: Megaphone,
  menu: Menu,
  "message-circle": MessageCircle,
  "message-square": MessageSquare,
  minus: Minus,
  "package-minus": PackageMinus,
  "panel-left-close": PanelLeftClose,
  "panel-left-open": PanelLeftOpen,
  pencil: Pencil,
  phone: Phone,
  play: Play,
  plus: Plus,
  "qr-code": QrCode,
  quote: Quote,
  "refresh-cw": RefreshCw,
  "rotate-3d": Rotate3d,
  "rotate-ccw": RotateCcw,
  ruler: Ruler,
  // Vocabulario de marca (cartel / QR) en `assets/icons/README.md`.
  "scan-line": ScanLine,
  school: School,
  search: Search,
  send: Send,
  "settings-2": Settings2,
  share: Share,
  "share-2": Share2,
  "shopping-bag": ShoppingBag,
  "shopping-cart": ShoppingCart,
  signpost: Signpost,
  smartphone: Smartphone,
  sparkles: Sparkles,
  "sun-moon": SunMoon,
  "train-front": TrainFront,
  "trash-2": Trash2,
  trees: Trees,
  "trending-down": TrendingDown,
  "trending-up": TrendingUp,
  truck: Truck,
  upload: Upload,
  user: User,
  "user-plus": UserPlus,
  "user-round-plus": UserRoundPlus,
  "user-x": UserX,
  users: Users,
  video: Video,
  view: View,
  whatsapp: WhatsApp,
  x: X,
} as const;

export type IconName = keyof typeof ICONS;

export interface IconProps extends Omit<React.ComponentPropsWithoutRef<"span">, "color"> {
  /** Nombre Lucide en kebab-case: "key", "qr-code", "line-chart". */
  name: IconName;
  /** 16 en línea con texto, 18 en controles, 20 en la barra lateral, 24 como marca de sección. */
  size?: number;
  strokeWidth?: number;
  /** Un token de color (`var(--text-danger)`). Sin él hereda `currentColor`. */
  color?: string;
}

/** Icono Lucide de trazo 1.5, decorativo (`aria-hidden`): el texto de al lado es el que habla. */
export function Icon({ name, size = 18, strokeWidth = 1.5, color, className, style, ...rest }: IconProps) {
  const Glyph = ICONS[name];
  return (
    <span
      aria-hidden="true"
      className={cx(s.icon, className)}
      style={color ? { ...style, color } : style}
      {...rest}
    >
      <Glyph size={size} strokeWidth={strokeWidth} absoluteStrokeWidth={false} />
    </span>
  );
}
